import {IMismoBureauData} from "./IMismoBureauData";
import {HTMLInputTypeAttribute} from "react";

export interface ICustomerCredit {
  id: string;
  creditScore: number;
  bureauScore: number;
  exScore: number;
  tuScore: number;
  eqScore: number;
  creditSummaryXml: string;
  customerId: string;
  deletedDate?: Date;
  createdDate: Date;
  modifiedDate: Date;
  acknowledgementDate?: Date;
  creditPulledDate?: Date;
  hardPull: boolean | undefined;
  mismoBureauData: IMismoBureauData;

  // General Applicant Info
  numberOfInquiriesInLast180Days: number;
  numberOfTimesSentToCollections: number;
  numberOfMonthsInBureauReport: number; // Number of months since the oldest trade
  applicantAgeInYears: number;

  // General Credit Line Info
  numberOf30Days: number; // Auto, Mortgage, and Student
  numberOf60Days: number; // Auto, Mortgage, and Student
  numberOf90PlusDays: number; // Auto, Mortgage, and Student
  numberOfLoansEver30DaysLate: number; // Auto, Mortgage, and Student
  numberOfLoansEver60DaysLate: number; // Auto, Mortgage, and Student
  numberOfLoansEver90DaysLate: number; // Auto, Mortgage, and Student
  numberOfCurrentTradeLines: number | undefined;
  numberOfTradeLines: number | undefined;
  numberOfAccountsPaid: number | undefined;
  numberOfHistoricallyNegative: number | undefined;
  numberOfCurrentlyNegative: number | undefined;
  monthlyPaymentForInstallmentAccounts: number; // All the monthly payments for installment loans
  monthlyPaymentForAllRevolvingAccounts: number; // All the monthly payments for credit cards, HELOCs, etc.
  monthlyPaymentForAllMortgageAccounts: number;
  monthlyPaymentForAllOtherAccounts: number;
  monthlyPaymentForAllLoans: number; // All the monthly payments for Auto, Mortgage, and Student

  // Bankruptcy
  numberOfBankruptcies: number;
  lastBankruptcyDate: Date | undefined;
  lastBankruptcyType: string;
  lastBankruptcyStatus: string;

  // Auto Loans
  numberOfAutosPaid: number | undefined;
  numberOfOpenAutos: number | undefined;
  lastAutoLoanNumberOf30DaysLate: number | undefined; // Number of payments 30 days late
  lastAutoLoanNumberOf60DaysLate: number | undefined; // Number of payments 60 days late
  lastAutoLoanNumberOfMonthsUntil30DaysLate: number | undefined; // Number months until the first payment 30 days late
  lastAutoLoanNumberOfMonthsUntil60DaysLate: number | undefined; // Number months until the first payment 60 days late
  lastAutoLoanPaymentPattern: string; // A month-by-month summary of every time they reported the loan status
  autoLoansMonthlyPayment: number; // Sum of all the monthly payments
  autoLoansBalanceRemaining: number; // Sum of all the balances

  // Repossessions
  numberOfRepossessions: number;
  lastRepossessionDate: Date | undefined;
  lastRepossessionMonthsIntoLoan: number | undefined; // How many months into the loan the repo occurred

  // Student Loans
  numberOfStudentLoans: number;
  studentLoansMonthlyPayment: number; // Sum of all the monthly payments
  studentLoansBalanceRemaining: number; // Sum of all the balances
}

export interface ICustomerCreditRow {
  itemTitle: string;
  toolTipText: string;
  variableName: keyof ICustomerCredit;
  inputType: HTMLInputTypeAttribute;
}

export interface ICustomerCreditSection {
  customerCreditRows: ICustomerCreditRow[];
  sectionTitle: string;
}

export const customerCreditSections: ICustomerCreditSection[] = [
  {
    sectionTitle: "Applicant Info",
    customerCreditRows: [
      {
        variableName: "numberOfInquiriesInLast180Days",
        itemTitle: "# of Inquiries in the Last 180 Days",
        toolTipText: "The number of credit inquiries in the last 180 days",
        inputType: "number"
      },
      {
        variableName: "numberOfTimesSentToCollections",
        itemTitle: "# of Times Sent to Collections",
        toolTipText: "The number of times they were sent to collections",
        inputType: "number"
      },
      {
        variableName: "numberOfMonthsInBureauReport",
        itemTitle: "# of Months in Bureau Report",
        toolTipText: "The length of the applicant's history with the bureau in months",
        inputType: "number"
      },
      {
        variableName: "applicantAgeInYears",
        itemTitle: "Applicant's Age in Years",
        toolTipText: "The applicant's age in years from the credit bureau",
        inputType: "number"
      }
    ]
  },
  {
    sectionTitle: "Credit Line",
    customerCreditRows: [
      {
        variableName: "numberOf30Days",
        itemTitle: "# of 30 Days",
        toolTipText: "The number of payments reported 30 days late",
        inputType: "number"
      },
      {
        variableName: "numberOf60Days",
        itemTitle: "# of 60 Days",
        toolTipText: "The number of payments reported 60 days late",
        inputType: "number"
      },
      {
        variableName: "numberOf90PlusDays",
        itemTitle: "# of 90+ Days",
        toolTipText: "The number of payments reported 90 or more days late",
        inputType: "number"
      },
      {
        variableName: "numberOfLoansEver30DaysLate",
        itemTitle: "# of Loans Ever 30 Days Late",
        toolTipText: "The number of loans that had a payment late by 30 days",
        inputType: "number"
      },
      {
        variableName: "numberOfLoansEver60DaysLate",
        itemTitle: "# of Loans Ever 60 Days Late",
        toolTipText: "The number of loans that had a payment late by 60 days",
        inputType: "number"
      },
      {
        variableName: "numberOfLoansEver90DaysLate",
        itemTitle: "# of Loans Ever 90 Days Late",
        toolTipText: "The number of loans that had a payment late by 90 days",
        inputType: "number"
      },
      {
        variableName: "numberOfCurrentTradeLines",
        itemTitle: "# of Current Trade Lines",
        toolTipText: "The number of current trade lines",
        inputType: "number"
      },
      {
        variableName: "numberOfTradeLines",
        itemTitle: "# of Trade Lines",
        toolTipText: "The total number of current trade lines",
        inputType: "number"
      },
      {
        variableName: "numberOfAccountsPaid",
        itemTitle: "# of Accounts Paid",
        toolTipText: "The total number of accounts paid",
        inputType: "number"
      },
      {
        variableName: "numberOfHistoricallyNegative",
        itemTitle: "# of Historically Negative",
        toolTipText: "The number of historically negative",
        inputType: "number"
      },
      {
        variableName: "numberOfCurrentlyNegative",
        itemTitle: "# of Currently Negative",
        toolTipText: "The number currently negative",
        inputType: "number"
      },
      {
        variableName: "monthlyPaymentForInstallmentAccounts",
        itemTitle: "Monthly Payment For All Installment Accounts",
        toolTipText: "The total of monthly payments across all installment accounts",
        inputType: "number"
      },
      {
        variableName: "monthlyPaymentForAllRevolvingAccounts",
        itemTitle: "Monthly Payment For All Revolving Accounts",
        toolTipText: "The total of monthly payments across all revolving accounts",
        inputType: "number"
      },
      {
        variableName: "monthlyPaymentForAllMortgageAccounts",
        itemTitle: "Monthly Payment For All Mortgage Accounts",
        toolTipText: "The total of monthly payments across all mortgage accounts",
        inputType: "number"
      },
      {
        variableName: "monthlyPaymentForAllOtherAccounts",
        itemTitle: "Monthly Payment For All Other Accounts",
        toolTipText: "The total of monthly payments across all other accounts",
        inputType: "number"
      },
      {
        variableName: "monthlyPaymentForAllLoans",
        itemTitle: "Monthly Payment For All Loans",
        toolTipText: "The total of monthly payments across all loans",
        inputType: "number"
      }
    ]
  },
  {
    sectionTitle: "Bankruptcy",
    customerCreditRows: [
      {
        variableName: "numberOfBankruptcies",
        itemTitle: "# of Bankruptcies",
        toolTipText: "The number of bankruptcies",
        inputType: "number"
      },
      {
        variableName: "lastBankruptcyDate",
        itemTitle: "Last Bankruptcy Date",
        toolTipText: "The date of the last bankruptcy",
        inputType: "date"
      },
      {
        variableName: "lastBankruptcyType",
        itemTitle: "Last Bankruptcy Type",
        toolTipText: "The type of the last bankruptcy",
        inputType: "text"
      },
      {
        variableName: "lastBankruptcyStatus",
        itemTitle: "Last Bankruptcy Status",
        toolTipText: "The status of the last bankruptcy",
        inputType: "text"
      }
    ]
  },
  {
    sectionTitle: "Auto Loan",
    customerCreditRows: [
      {
        variableName: "numberOfAutosPaid",
        itemTitle: "# of Autos Paid",
        toolTipText: "The number of auto loans paid",
        inputType: "number"
      },
      {
        variableName: "numberOfOpenAutos",
        itemTitle: "# of Open Autos",
        toolTipText: "The number of auto loans open",
        inputType: "number"
      },
      {
        variableName: "lastAutoLoanNumberOf30DaysLate",
        itemTitle: "Last Auto Loan # of 30 Days",
        toolTipText: "The number of payments 30 days late on last auto loan",
        inputType: "number"
      },
      {
        variableName: "lastAutoLoanNumberOf60DaysLate",
        itemTitle: "Last Auto Loan # of 60 Days",
        toolTipText: "The number of payments 60 days late on last auto loan",
        inputType: "number"
      },
      {
        variableName: "lastAutoLoanNumberOfMonthsUntil30DaysLate",
        itemTitle: "Last Auto Loan # Months Until 30 Days Late",
        toolTipText: "The number of months from account open to 30 days late",
        inputType: "number"
      },
      {
        variableName: "lastAutoLoanNumberOfMonthsUntil60DaysLate",
        itemTitle: "Last Auto Loan # Months Until 60 Days Late",
        toolTipText: "The number of months from account open to 60 days late",
        inputType: "number"
      },
      {
        variableName: "autoLoansMonthlyPayment",
        itemTitle: "Monthly Payment For Auto Loans",
        toolTipText: "The total of monthly payments across all auto loans",
        inputType: "number"
      },
      {
        variableName: "autoLoansBalanceRemaining",
        itemTitle: "Auto Loans Balance Remaining",
        toolTipText: "The total balance across all auto loans",
        inputType: "number"
      }
    ]
  },
  {
    sectionTitle: "Repossession",
    customerCreditRows: [
      {
        variableName: "numberOfRepossessions",
        itemTitle: "# of Repossessions",
        toolTipText: "The number of repossessions",
        inputType: "number"
      },
      {
        variableName: "lastRepossessionDate",
        itemTitle: "Last Repossession Date",
        toolTipText: "The date that the last repossession was reported",
        inputType: "date"
      },
      {
        variableName: "lastRepossessionMonthsIntoLoan",
        itemTitle: "Last Repossession Months Into Loan",
        toolTipText: "How many months into the loan the repossession was reported",
        inputType: "number"
      }
    ]
  },
  {
    sectionTitle: "Student Loan",
    customerCreditRows: [
      {
        variableName: "numberOfStudentLoans",
        itemTitle: "# of Student Loans",
        toolTipText: "The number of student loans",
        inputType: "number"
      },
      {
        variableName: "studentLoansMonthlyPayment",
        itemTitle: "Total Monthly Payment",
        toolTipText: "The total of all monthly payments for all student loans",
        inputType: "number"
      },
      {
        variableName: "studentLoansBalanceRemaining",
        itemTitle: "Total Balance Remaining",
        toolTipText: "The total of all balances remaining for all student loans",
        inputType: "number"
      }
    ]
  },
];

// Functions that takes a customer's credit object and a row item title. It returns the value of the row item title or undefined if it doesn't exist.
export const getCustomerCreditValue = (customerCredit: ICustomerCredit, itemTitle: string): any => {
  const customerCreditRow = customerCreditSections.flatMap(section => section.customerCreditRows).find(row => row.itemTitle.toLowerCase() === itemTitle.toLowerCase());
  if (customerCreditRow) {
    return customerCredit[customerCreditRow.variableName];
  }
  return undefined;
}